/**
 * גבייה — invoices, the 30/60/90/90+ aging view, and payment recording.
 *
 * The aging figure is `total − paid`, not the invoice total. A partially paid invoice
 * reporting its full value overstates the debt, which is the classic way an aging report
 * loses the firm's trust on first use.
 *
 * Scope boundary from the terms page: this tracks what the firm is owed. It is not
 * bookkeeping software and does not issue tax invoices, so the document is a fee demand
 * and the wording says so.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatMonthName } from "@onecpa/shared";
import {
  Badge,
  Button,
  type Column,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  KpiStrip,
  Money,
  PageHeader,
  Panel,
  Select,
  StatusBadge,
  TabStrip,
} from "@onecpa/ui";
import { api, type InvoiceRow } from "../api/client.js";
import { agorot, usePeriod } from "../app/hooks.js";
import { useRoleGate } from "../app/hooks.js";
import { useSession } from "../app/session.js";

const BUCKET_LABELS: Record<string, string> = {
  current: "בתוך המועד",
  up_to_30: "עד 30 יום",
  up_to_60: "31–60 יום",
  up_to_90: "61–90 יום",
  over_90: "מעל 90 יום",
};

type TabId = "aging" | "invoices" | "schedules";

export function Billing() {
  const session = useSession();
  const queryClient = useQueryClient();
  const { allowed } = useRoleGate(["owner", "manager"]);
  const { period, label, next, previous } = usePeriod();
  const [tab, setTab] = useState<TabId>("aging");
  const [payingInvoice, setPayingInvoice] = useState<InvoiceRow | null>(null);

  const aging = useQuery({
    queryKey: ["billing", "aging", session.orgId],
    queryFn: () => api.billing.aging(session.orgId),
    enabled: allowed,
  });

  const invoices = useQuery({
    queryKey: ["billing", "invoices", session.orgId],
    queryFn: () => api.billing.invoices(session.orgId),
    enabled: allowed,
  });

  const schedules = useQuery({
    queryKey: ["billing", "schedules", session.orgId],
    queryFn: () => api.billing.feeSchedules(session.orgId),
    enabled: allowed && tab === "schedules",
  });

  const generate = useMutation({
    mutationFn: () => api.billing.generate(session.orgId, period.year, period.month),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      session.toast(
        result.created > 0
          ? `נוצרו ${result.created} טיוטות חשבון`
          : "כל החשבונות לחודש זה כבר קיימים",
      );
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const issue = useMutation({
    mutationFn: (id: string) => api.billing.issue(session.orgId, id),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      session.toast(`החשבון הופק במספר ${result.invoice.number}`);
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  if (!allowed) {
    return (
      <ErrorState title="אין הרשאה למסך זה" message="גבייה זמינה לבעל המשרד ולמנהל המשרד בלבד." />
    );
  }

  const buckets = aging.data?.buckets ?? {};

  const invoiceColumns: Column<InvoiceRow>[] = [
    {
      key: "number",
      header: "מספר",
      sticky: true,
      width: "110px",
      cell: (row) => (
        <bdi className="tnum text-xs font-semibold text-ink">
          {row.number ? `#${row.number}` : "טיוטה"}
        </bdi>
      ),
    },
    {
      key: "client",
      header: "לקוח",
      cell: (row) => (
        <Link
          to="/clients/$clientId"
          params={{ clientId: row.clientId }}
          className="link-underline text-xs text-ink"
        >
          {row.clientName}
        </Link>
      ),
    },
    {
      key: "period",
      header: "תקופה",
      hideOnSmall: true,
      cell: (row) =>
        row.periodMonth ? (
          <span className="text-xs text-ink-muted">
            {formatMonthName(row.periodMonth)} {row.periodYear}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "due",
      header: "לתשלום עד",
      hideOnSmall: true,
      cell: (row) =>
        row.dueDate ? (
          <bdi className="tnum text-xs text-ink-muted">{formatDate(row.dueDate)}</bdi>
        ) : (
          "—"
        ),
    },
    {
      key: "total",
      header: "סכום",
      align: "end",
      cell: (row) => <Money agorot={agorot(row.total)} colorize={false} />,
    },
    {
      key: "paid",
      header: "שולם",
      align: "end",
      cell: (row) => {
        const paid = agorot(row.paidAmount);
        const total = agorot(row.total);
        return paid === 0 ? (
          <span className="text-xs text-ink-faint">—</span>
        ) : (
          <span className="flex items-center justify-end gap-1.5">
            <Money agorot={paid} colorize={false} />
            {paid < total && <Badge tone="warning">חלקי</Badge>}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "מצב",
      align: "end",
      cell: (row) => (
        <span className="flex items-center justify-end gap-2">
          <StatusBadge vocabulary="invoice" status={row.status === "draft" ? "sent" : row.status} />
          {row.status === "draft" && (
            <Button size="sm" variant="primary" onClick={() => issue.mutate(row.id)}>
              הפקה
            </Button>
          )}
          {(row.status === "sent" || row.status === "overdue") && (
            <Button size="sm" onClick={() => setPayingInvoice(row)}>
              רישום תשלום
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="גבייה"
        period={
          <span className="inline-flex items-center gap-1.5">
            <button type="button" onClick={previous} aria-label="חודש קודם" className="px-1">
              ‹
            </button>
            {label}
            <button type="button" onClick={next} aria-label="חודש הבא" className="px-1">
              ›
            </button>
          </span>
        }
        action={
          <Button
            variant="primary"
            icon="plus"
            loading={generate.isPending}
            onClick={() => generate.mutate()}
          >
            הפקת חשבונות לחודש
          </Button>
        }
      >
        <TabStrip
          tabs={[
            { id: "aging", label: "גיול חוב" },
            { id: "invoices", label: "חשבונות", count: invoices.data?.invoices.length },
            { id: "schedules", label: "ריטיינרים" },
          ]}
          active={tab}
          onSelect={(id) => setTab(id as TabId)}
        />
      </PageHeader>

      <KpiStrip
        items={[
          {
            label: "חוב פתוח",
            value: aging.data ? (
              <Money agorot={aging.data.totalOutstandingAgorot} colorize={false} />
            ) : undefined,
            tone: "ochre",
          },
          ...(["up_to_30", "up_to_60", "up_to_90", "over_90"] as const).map((key) => ({
            label: BUCKET_LABELS[key] ?? key,
            value: aging.data ? (
              <Money agorot={buckets[key]?.amountAgorot ?? 0} colorize={false} />
            ) : undefined,
            tone: (key === "over_90" ? "red" : key === "up_to_90" ? "ochre" : "ink") as
              "red" | "ochre" | "ink",
          })),
        ]}
      />

      <div className="p-5">
        {tab === "aging" && (
          <DataTable
            columns={[
              ...invoiceColumns.slice(0, 4),
              {
                key: "overdue",
                header: "ימי פיגור",
                align: "center",
                cell: (row) => {
                  const detail = aging.data?.invoices.find((item) => item.id === row.id);
                  return (
                    <bdi
                      className={`tnum text-xs ${
                        (detail?.daysOverdue ?? 0) > 90
                          ? "font-semibold text-red-ink"
                          : "text-ink-muted"
                      }`}
                    >
                      {detail?.daysOverdue && detail.daysOverdue > 0 ? detail.daysOverdue : "—"}
                    </bdi>
                  );
                },
              },
              {
                key: "outstanding",
                header: "יתרה",
                align: "end",
                cell: (row) => {
                  const detail = aging.data?.invoices.find((item) => item.id === row.id);
                  return <Money agorot={detail?.outstandingAgorot ?? 0} colorize={false} />;
                },
              },
              invoiceColumns[invoiceColumns.length - 1]!,
            ]}
            rows={aging.data?.invoices ?? []}
            rowKey={(row) => row.id}
            loading={aging.isLoading}
            caption="חשבונות פתוחים לפי גיל החוב"
            footnote="היתרה היא הסכום לתשלום פחות מה ששולם · מעל 90 יום נפתחת משימה אוטומטית להחלטה"
            empty={
              <EmptyState
                title="אין חוב פתוח"
                icon="check"
                description="כל החשבונות שהופקו שולמו."
              />
            }
          />
        )}

        {tab === "invoices" && (
          <DataTable
            columns={invoiceColumns}
            rows={invoices.data?.invoices ?? []}
            rowKey={(row) => row.id}
            loading={invoices.isLoading}
            density="compact"
            caption="כל החשבונות"
            empty={
              <EmptyState
                title="אין חשבונות"
                description="הפקת חשבונות לחודש יוצרת טיוטה לכל לקוח עם ריטיינר פעיל."
                action={
                  <Button variant="primary" onClick={() => generate.mutate()}>
                    הפקת חשבונות לחודש
                  </Button>
                }
              />
            }
          />
        )}

        {tab === "schedules" && (
          <Panel title="ריטיינרים פעילים">
            {(schedules.data?.schedules ?? []).length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {(schedules.data?.schedules ?? []).map((schedule) => (
                  <li key={schedule.id} className="flex items-center gap-3 px-3.5 py-2.5">
                    <Link
                      to="/clients/$clientId"
                      params={{ clientId: schedule.clientId }}
                      className="link-underline min-w-0 flex-1 truncate text-xs font-semibold text-ink"
                    >
                      {schedule.clientName}
                    </Link>
                    <span className="text-2xs text-ink-faint">{schedule.label}</span>
                    <Money agorot={agorot(schedule.amount)} colorize={false} />
                    {schedule.isActive ? (
                      <Badge tone="success">פעיל</Badge>
                    ) : (
                      <Badge tone="neutral">לא פעיל</Badge>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="אין ריטיינרים" description="הגדירו ריטיינר בכרטיס הלקוח." />
            )}
          </Panel>
        )}
      </div>

      <PaymentDialog invoice={payingInvoice} onClose={() => setPayingInvoice(null)} />
    </>
  );
}

function PaymentDialog({ invoice, onClose }: { invoice: InvoiceRow | null; onClose(): void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const outstanding = invoice ? agorot(invoice.total) - agorot(invoice.paidAmount) : 0;
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");

  const record = useMutation({
    mutationFn: () =>
      api.billing.recordPayment(session.orgId, invoice!.id, {
        amount: Number(amount),
        method,
        receivedOn,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      session.toast(result.settled ? "החשבון סומן כשולם" : "התשלום נרשם");
      setAmount("");
      onClose();
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  return (
    <Dialog
      open={invoice !== null}
      onClose={onClose}
      title="רישום תשלום"
      description={
        invoice ? `${invoice.clientName} · חשבון #${invoice.number ?? "טיוטה"}` : undefined
      }
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button
            variant="primary"
            disabled={!amount || Number(amount) <= 0}
            loading={record.isPending}
            onClick={() => record.mutate()}
          >
            רישום
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <p className="text-xs text-ink-muted">
          יתרה לתשלום: <Money agorot={outstanding} colorize={false} />
        </p>

        <Field label="סכום (₪)" required>
          {({ id }) => (
            <Input
              id={id}
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder={(outstanding / 100).toFixed(2)}
            />
          )}
        </Field>

        <Field label="אמצעי תשלום">
          {({ id }) => (
            <Select id={id} value={method} onChange={(event) => setMethod(event.target.value)}>
              <option value="bank_transfer">העברה בנקאית</option>
              <option value="credit_card">כרטיס אשראי</option>
              <option value="cheque">שיק</option>
              <option value="cash">מזומן</option>
              <option value="direct_debit">הוראת קבע</option>
              <option value="other">אחר</option>
            </Select>
          )}
        </Field>

        <Field label="תאריך קבלה" required>
          {({ id }) => (
            <Input
              id={id}
              type="date"
              value={receivedOn}
              onChange={(event) => setReceivedOn(event.target.value)}
            />
          )}
        </Field>

        <Field label="אסמכתא">
          {({ id }) => (
            <Input
              id={id}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
